import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Select,
  MenuItem,
  TableFooter,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import { axiosInstance, useClientInfo, useAppContext } from "../utils/commonImports";
import MyDatePicker from "../components/MyDatePicker";
import { FormControl, InputLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFYRange, resolveDateRange, toISODate, printPdfDoc } from "../utils/fy";

/* ===============================
   HELPERS
================================ */
const fmtDate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "");

const fmt = (v) => (v === null || v === undefined ? "" : v);

// ✅ FORMAT TIME TO HH:mm
const fmtTime = (v) => {
  if (!v) return "";

  // If already HH:mm or HH:mm:ss
  if (typeof v === "string") {
    // handles "10:30", "10:30:00"
    const parts = v.split(":");
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    }
  }

  return v;
};

const compactHeaderCell = {
  backgroundColor: "#f2f2f2",
  fontWeight: 600,
  fontSize: "0.6rem", // 🔽 smaller font
  lineHeight: "1.1rem", // 🔽 tighter height
  padding: "2px 4px", // 🔽 reduce padding
  border: "1px solid #ccc",
  textAlign: "center",
};

const compactSelectSx = {
  minWidth: 200,
  "& .MuiInputBase-root": {
    height: 32,
    fontSize: "0.75rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
  },
};

const tableCellStyle = {
  fontSize: "0.72rem",
  padding: "4px 6px",
  border: "1px solid #ddd",
};

const headerStyle = {
  backgroundColor: "#f4f6f8",
  fontWeight: 700,
  fontSize: "0.72rem",
  border: "1px solid #ccc",
};

export default function TripStatementReport() {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [reportType, setReportType] = useState("DATE_WISE");
  const isNumberWise = reportType === "NUMBER_WISE";

  const [vehicleGroup, setVehicleGroup] = useState("ALL");
  const [customer, setCustomer] = useState("ALL");
  const [vehicleFilter, setVehicleFilter] = useState("ALL");

  const FILTER_HEIGHT = 32;
  const FILTER_FONT = "0.75rem";

  const reportTitleMap = {
    DATE_WISE: "Tripsheet Statement : Date Wise",
    NUMBER_WISE: "Tripsheet Statement : Tripsheet No Wise",
    CUSTOMER_WISE: "Tripsheet Statement : Customer Wise",
    VEHICLE_GROUP_WISE: "Tripsheet Statement : Vehicle Group Wise",
     };

  /* ===============================
     LOAD REPORT DATA
     API currently returns the client's tripsheet report rows.
     FY/date filtering is applied consistently on the client below.
  ================================ */
  const loadReport = async () => {
    if (!clientId || financialYearLoading) return;

    setLoading(true);
    setReportError("");

    try {
      // Keep the existing report API contract unchanged: it currently
      // loads report rows by client. Selected FY and date filters are applied
      // locally below, using the shared FY helper.
      const response = await axiosInstance.get("/api/tripsheets/report", {
        params: { client_id: clientId },
      });

      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.rows)
            ? response.data.rows
            : null;

      if (!data) {
        throw new Error("Invalid response received from Tripsheet Report API.");
      }

      setRows(data);
    } catch (error) {
      console.error(
        "Failed to load Tripsheet Statement Report:",
        error.response?.data || error
      );
      setRows([]);
      setReportError(
        error.response?.data?.message ||
        error.message ||
        "Unable to load Tripsheet Statement Report."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientId || financialYearLoading || !selectedFinancialYear) return;
    setFromDate(null);
    setToDate(null);
    // Load all rows for the client; FY filtering is applied below using the
    // centrally selected FY dates, so this remains compatible with the API.
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading]);

  console.log(
    "Sample vehicle group values:",
    rows.slice(0, 10).map((r) => r.assigned_vehicle_group_name),
  );

  useEffect(() => {
    if (
      reportType !== "VEHICLE_GROUP_WISE" &&
      reportType !== "CUSTOMER_WISE" 
          ) {
      setVehicleGroup("ALL");
    }
  }, [reportType]);

  const filteredRows = rows
    .map((r) => ({
      ...r,
      runKm: (r.close_km_reading || 0) - (r.open_km_reading || 0),
    }))
    .filter((r) => {
      if (!r.open_date) return false;

      const rowDate = dayjs(r.open_date);
      const { start: fyStart, end: fyEnd } = getFYRange(selectedFinancialYear);
      const rowISODate = toISODate(r.open_date);

      // Mandatory selected Financial Year filter
      if (fyStart && rowISODate < fyStart) return false;
      if (fyEnd && rowISODate > fyEnd) return false;

      // User-selected date filters, constrained to the selected FY
      const { from: effectiveFrom, to: effectiveTo } = resolveDateRange(
        selectedFinancialYear,
        fromDate,
        toDate,
        false
      );
      if (effectiveFrom && rowDate.isBefore(dayjs(effectiveFrom), "day")) return false;
      if (effectiveTo && rowDate.isAfter(dayjs(effectiveTo), "day")) return false;

      // Vehicle Group filter (works for ALL reports)
      if (
        vehicleGroup !== "ALL" &&
        (r.assigned_vehicle_group_name || "").trim().toLowerCase() !==
          vehicleGroup.trim().toLowerCase()
      ) {
        return false;
      }

      // Customer filter (works for ALL reports)
      if (
        customer !== "ALL" &&
        (r.customer_name || "").trim().toLowerCase() !==
          customer.trim().toLowerCase()
      ) {
        return false;
      }

      // Vehicle / Vendor filter
      if (
        vehicleFilter !== "ALL" &&
        (r.vehicle_display || "").trim().toLowerCase() !==
          vehicleFilter.trim().toLowerCase()
      ) {
        return false;
      }

      return true;
    });

  const vehicleOptions = [
    "ALL",
    ...Array.from(
      new Set(
        rows
          .map((r) => r.vehicle_display)
          .filter(Boolean)
          .map((v) => v.trim()),
      ),
    ),
  ];

  const vehicleGroupOptions = [
    "ALL",
    ...Array.from(
      new Set(
        rows
          .map((r) => r.assigned_vehicle_group_name)
          .filter(Boolean)
          .map((g) => g.trim()),
      ),
    ),
  ];

  const customerOptions = [
    "ALL",
    ...Array.from(
      new Set(
        rows
          .map((r) => r.customer_name)
          .filter(Boolean)
          .map((c) => c.trim()),
      ),
    ),
  ];

  // ✅ GROUP BY VEHICLE GROUP (MUST BE ABOVE renderDateWiseTable)
  const groupByVehicleGroup = (rows) =>
    rows.reduce((acc, r) => {
      const key = r.assigned_vehicle_group_name || "UNASSIGNED";
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});

  const groupByCustomer = (rows) =>
    rows.reduce((acc, r) => {
      const key = r.customer_name || "UNKNOWN CUSTOMER";
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});

  const renderRow = (r, i, isNumberWise) => {
    const runKm = (r.close_km_reading || 0) - (r.open_km_reading || 0);

    return (
      <TableRow
        key={i}
        hover
        sx={{
          backgroundColor: i % 2 === 0 ? "#ffffff" : "#fafafa",
          "& td": {
            border: "1px solid #ddd",
            fontSize: "0.65rem",
            padding: "4px 6px",
          },
        }}
      >
        {isNumberWise ? (
          <>
            <TableCell>{r.tripsheet_number}</TableCell>
            <TableCell>{fmtDate(r.open_date)}</TableCell>
          </>
        ) : (
          <>
            <TableCell>{fmtDate(r.open_date)}</TableCell>
            <TableCell>{r.tripsheet_number}</TableCell>
          </>
        )}

        <TableCell>{r.customer_name}</TableCell>
        <TableCell>{r.duty}</TableCell>
        <TableCell>{r.vehicle_display}</TableCell>
        <TableCell>{r.assigned_vehicle_group_name}</TableCell>

        <TableCell align="right">{r.open_km_reading}</TableCell>
        <TableCell>{fmtTime(r.open_time)}</TableCell>
        <TableCell align="right">{r.close_km_reading}</TableCell>
        <TableCell>{fmtTime(r.close_time)}</TableCell>

        <TableCell align="right">{r.trip_charges_days_used}</TableCell>
        <TableCell align="right">{runKm}</TableCell>
        <TableCell align="right">{r.trip_charges_hours_used}</TableCell>

        <TableCell align="center">{formatHrsKms(r)}</TableCell>
        <TableCell align="right">{r.trip_charges_charge_amount}</TableCell>

        <TableCell align="right">{fmt(r.trip_charges_extra_hour)}</TableCell>
        <TableCell align="right">
          {fmt(r.trip_charges_slab_extra_hour_rate)}
        </TableCell>
        <TableCell align="right">
          {fmt(r.trip_charges_extra_hour_amount)}
        </TableCell>

        <TableCell align="right">{fmt(r.trip_charges_extra_kms)}</TableCell>
        <TableCell align="right">
          {fmt(r.trip_charges_slab_extra_km_rate)}
        </TableCell>
        <TableCell align="right">
          {fmt(r.trip_charges_extra_km_amount)}
        </TableCell>

        <TableCell align="right">{r.trip_charges_hire_amount}</TableCell>

        <TableCell align="right">{r.trip_charges_local_batta}</TableCell>
        <TableCell align="right">{r.trip_charges_others}</TableCell>
        <TableCell align="right">{r.trip_charges_parking}</TableCell>
        <TableCell align="right">{r.trip_charges_permit}</TableCell>
        <TableCell align="right">{fmt(r.trip_charges_toll_fasttag)}</TableCell>
        <TableCell align="right">{fmt(r.trip_charges_escort_amount)}</TableCell>
        <TableCell align="right">
          {fmt(r.trip_charges_fuel_esculation_amount)}
        </TableCell>
        <TableCell align="right">
          {(() => {
            const total =
              Number(r.trip_charges_advance || 0) +
              Number(r.trip_charges_booking_advance || 0);

            return total ? Math.round(total) : "";
          })()}
        </TableCell>

        <TableCell align="right">{r.customer_total}</TableCell>
      </TableRow>
    );
  };

  /* ===============================
     DATE WISE TABLE
  ================================ */
  const renderDateWiseTable = () => (
    // <TableContainer sx={{ maxHeight: "80vh" }}>
    <TableContainer
      sx={{
        maxWidth: 1280,
        overflowX: "auto",

        /* ===== THIN HORIZONTAL SCROLLBAR ===== */
        "&::-webkit-scrollbar": {
          height: "6px", // ✅ small height
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#b0b0b0",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#888",
        },

        /* Firefox */
        scrollbarWidth: "thin", // ✅ thin scrollbar
        scrollbarColor: "#b0b0b0 #f1f1f1",
      }}
    >
      <Table
        size="small"
        sx={{
          width: "100%",
          // tableLayout: "fixed", // 🔑 IMPORTANT
          "& th, & td": {
            fontSize: "0.65rem",
            padding: "4px 6px",
            wordBreak: "break-word",
          },
        }}
      >
        {/* ===== GROUP HEADER ===== */}
        <TableHead>
          {/* ===== FIRST HEADER ROW ===== */}
          <TableRow
            sx={{
              "& th": {
                backgroundColor: "#f2f2f2",
                fontWeight: 600,
                fontSize: "0.65rem",
                border: "1px solid #ccc",
                textAlign: "center",
                padding: "4px 6px",
              },
            }}
          >
            {isNumberWise ? (
              <>
                <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 90 }}>
                  Tripsheet#
                </TableCell>
                <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 70 }}>
                  Date
                </TableCell>
              </>
            ) : (
              <>
                <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 65 }}>
                  Date
                </TableCell>
                <TableCell
                  rowSpan={2}
                  sx={{
                    ...compactHeaderCell,
                    width: 85,
                    whiteSpace: "normal",
                    textAlign: "center",
                  }}
                >
                  Tripsheet
                  <br />
                  No.
                </TableCell>
              </>
            )}
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 160 }}>
              Customer
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 80 }}>
              Duty
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 110 }}>
              Vehicle
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 90 }}>
              Vehicle Group
            </TableCell>

            <TableCell colSpan={2} sx={{ ...compactHeaderCell, width: 90 }}>
              Opening
            </TableCell>
            <TableCell colSpan={2} sx={{ ...compactHeaderCell, width: 95 }}>
              Closing
            </TableCell>
            <TableCell colSpan={3} sx={{ ...compactHeaderCell, width: 120 }}>
              Usage
            </TableCell>

            <TableCell
              rowSpan={2}
              sx={{
                ...compactHeaderCell,
                width: 50,
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Tariff
              <br />
              Slab
            </TableCell>

            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 60 }}>
              Rate
            </TableCell>
            {/* Extra Hours */}
            <TableCell colSpan={3} sx={{ ...compactHeaderCell, width: 145 }}>
              Extra Hours
            </TableCell>

            {/* Extra Kms */}
            <TableCell colSpan={3} sx={{ ...compactHeaderCell, width: 145 }}>
              Extra Kms
            </TableCell>

            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 60 }}>
              Hire
            </TableCell>

            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Batta
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Others
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Parking
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Permit
            </TableCell>
            {/* Fuel Escalation */}

            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Toll
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 50 }}>
              Escort
            </TableCell>
            <TableCell
              rowSpan={2}
              sx={{
                ...compactHeaderCell,
                width: 50,
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Fuel
              <br />
              Esc.
            </TableCell>

            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 60 }}>
              Advance
            </TableCell>
            <TableCell rowSpan={2} sx={{ ...compactHeaderCell, width: 70 }}>
              Amount
            </TableCell>
          </TableRow>

          {/* ===== SECOND HEADER ROW ===== */}
          <TableRow
            sx={{
              "& th": {
                backgroundColor: "#f2f2f2",
                fontWeight: 600,
                fontSize: "0.65rem",
                border: "1px solid #ccc",
                textAlign: "center",
                padding: "4px 6px",
              },
            }}
          >
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Km</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 60 }}>Time</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Km</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 60 }}>Time</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Days</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 80 }}>Kms</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 80 }}>
              Hours
            </TableCell>

            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Hrs</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Rate</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>
              Amount
            </TableCell>

            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Kms</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>Rate</TableCell>
            <TableCell sx={{ ...compactHeaderCell, width: 70 }}>
              Amount
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={32} align="center" sx={{ py: 2 }}>
                <Box display="inline-flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading Tripsheet Statement...</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!loading && !reportError && filteredRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={32} align="center" sx={{ py: 2 }}>
                No tripsheet records found for the selected Financial Year / date range.
              </TableCell>
            </TableRow>
          )}
          {/* ================= VEHICLE GROUP WISE ================= */}
          {reportType === "VEHICLE_GROUP_WISE" && vehicleGroup === "ALL"
            ? Object.entries(groupByVehicleGroup(filteredRows)).map(
                ([groupName, groupRows]) => {
                  const gt = calculateTotals(groupRows);

                  const groupBatta =
                    gt.trip_charges_local_batta +
                    gt.trip_charges_no_of_os_batta +
                    gt.trip_charges_os_batta_total_amount +
                    gt.trip_charges_no_of_os_night_batta +
                    gt.trip_charges_os_night_batta_total_amount;

                  const groupAdvance =
                    gt.trip_charges_advance + gt.trip_charges_booking_advance;

                  return (
                    <React.Fragment key={groupName}>
                      {/* GROUP HEADER */}
                      <TableRow>
                        <TableCell colSpan={32} sx={{ fontWeight: 700 }}>
                          {groupName}
                        </TableCell>
                      </TableRow>

                      {groupRows.map((r, i) => renderRow(r, i, isNumberWise))}

                      {/* ✅ GROUP TOTAL */}
                      {renderGroupTotalRow(gt, groupBatta, groupAdvance)}
                    </React.Fragment>
                  );
                },
              )
            : reportType === "CUSTOMER_WISE"
              ? customer === "ALL"
                ? /* ================= CUSTOMER = ALL ================= */
                  Object.entries(groupByCustomer(filteredRows)).map(
                    ([customerName, groupRows]) => {
                      const gt = calculateTotals(groupRows);
                      const groupBatta =
                        gt.trip_charges_local_batta +
                        gt.trip_charges_no_of_os_batta +
                        gt.trip_charges_os_batta_total_amount +
                        gt.trip_charges_no_of_os_night_batta +
                        gt.trip_charges_os_night_batta_total_amount;

                      const groupAdvance =
                        gt.trip_charges_advance +
                        gt.trip_charges_booking_advance;

                      return (
                        <React.Fragment key={customerName}>
                          <TableRow>
                            <TableCell colSpan={32} sx={{ fontWeight: 700 }}>
                              {customerName}
                            </TableCell>
                          </TableRow>

                          {groupRows.map((r, i) =>
                            renderRow(r, i, isNumberWise),
                          )}

                          {/* ✅ GROUP TOTAL */}
                          {renderGroupTotalRow(gt, groupBatta, groupAdvance)}
                        </React.Fragment>
                      );
                    },
                  )
                : vehicleGroup === "ALL"
                  ? /* ================= CUSTOMER = X, VEHICLE GROUP = ALL ================= */
                    Object.entries(groupByVehicleGroup(filteredRows)).map(
                      ([groupName, groupRows]) => {
                        const gt = calculateTotals(groupRows);
                        const groupBatta =
                          gt.trip_charges_local_batta +
                          gt.trip_charges_no_of_os_batta +
                          gt.trip_charges_os_batta_total_amount +
                          gt.trip_charges_no_of_os_night_batta +
                          gt.trip_charges_os_night_batta_total_amount;

                        const groupAdvance =
                          gt.trip_charges_advance +
                          gt.trip_charges_booking_advance;

                        return (
                          <React.Fragment key={groupName}>
                            <TableRow>
                              <TableCell colSpan={32} sx={{ fontWeight: 700 }}>
                                {groupName}
                              </TableCell>
                            </TableRow>

                            {groupRows.map((r, i) =>
                              renderRow(r, i, isNumberWise),
                            )}

                            {/* ✅ GROUP TOTAL (THIS WAS MISSING) */}
                            {renderGroupTotalRow(gt, groupBatta, groupAdvance)}
                          </React.Fragment>
                        );
                      },
                    )
                  : /* ================= CUSTOMER = X, VEHICLE GROUP = Y ================= */
                    filteredRows.map((r, i) => renderRow(r, i, isNumberWise))
              : /* ================= OTHER REPORTS ================= */
                filteredRows.map((r, i) => renderRow(r, i, isNumberWise))}
        </TableBody>

        <TableFooter>
          <TableRow
            sx={{
              backgroundColor: "#f9fafb",

              /* 🔥 TRUE double top border */
              borderTop: "4px double #333",

              "& td": {
                border: "1px solid #bbb",
                fontWeight: 700,
                fontSize: "0.70rem",
                padding: "4px 6px",

                /* Remove cell top border so double line is clean */
                borderTop: "none",
              },
            }}
          >
            <TableCell colSpan={6}>TOTAL</TableCell>

            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />

            <TableCell align="right">
              {int(totals.trip_charges_days_used)}
            </TableCell>
            <TableCell align="right">{int(totals.runKm)}</TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_hours_used)}
            </TableCell>

            <TableCell align="center">—</TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_charge_amount)}
            </TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_extra_hour)}
            </TableCell>
            <TableCell align="center">—</TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_extra_hour_amount)}
            </TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_extra_kms)}
            </TableCell>
            <TableCell align="center">—</TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_extra_km_amount)}
            </TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_hire_amount)}
            </TableCell>

            <TableCell align="right">
              {int(
                totals.trip_charges_local_batta +
                  totals.trip_charges_no_of_os_batta +
                  totals.trip_charges_os_batta_total_amount +
                  totals.trip_charges_no_of_os_night_batta +
                  totals.trip_charges_os_night_batta_total_amount,
              )}
            </TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_others)}
            </TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_parking)}
            </TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_permit)}
            </TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_toll_fasttag)}
            </TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_escort_amount)}
            </TableCell>
            <TableCell align="right">
              {int(totals.trip_charges_fuel_esculation_amount)}
            </TableCell>

            <TableCell align="right">
              {int(
                totals.trip_charges_advance +
                  totals.trip_charges_booking_advance,
              )}
            </TableCell>

            <TableCell align="right">
              {int(totals.trip_charges_charge_amount)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
  
  const renderGroupTotalRow = (gt, groupBatta, groupAdvance) => (
    <TableRow
      sx={{
        backgroundColor: "#eef2f7",
        "& td": {
          fontWeight: 700,
          borderTop: "2px solid #555",
          fontSize: "0.65rem",
        },
      }}
    >
      <TableCell colSpan={6}>Group Total</TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell align="right">{gt.trip_charges_days_used}</TableCell>
      <TableCell align="right">{gt.runKm}</TableCell>
      <TableCell align="right">{gt.trip_charges_hours_used}</TableCell>
      <TableCell align="center">—</TableCell>
      <TableCell align="right">{gt.trip_charges_charge_amount}</TableCell>
      <TableCell align="right">{gt.trip_charges_extra_hour}</TableCell>
      <TableCell align="center">—</TableCell>
      <TableCell align="right">{gt.trip_charges_extra_hour_amount}</TableCell>
      <TableCell align="right">{gt.trip_charges_extra_kms}</TableCell>
      <TableCell align="center">—</TableCell>
      <TableCell align="right">{gt.trip_charges_extra_km_amount}</TableCell>
      <TableCell align="right">{gt.trip_charges_hire_amount}</TableCell>
      <TableCell align="right">{groupBatta}</TableCell>
      <TableCell align="right">{gt.trip_charges_others}</TableCell>
      <TableCell align="right">{gt.trip_charges_parking}</TableCell>
      <TableCell align="right">{gt.trip_charges_permit}</TableCell>
      <TableCell align="right">{gt.trip_charges_toll_fasttag}</TableCell>
      <TableCell align="right">{gt.trip_charges_escort_amount}</TableCell>
      <TableCell align="right">
        {gt.trip_charges_fuel_esculation_amount}
      </TableCell>
      <TableCell align="right">{groupAdvance}</TableCell>
      <TableCell align="right">{gt.trip_charges_charge_amount}</TableCell>
    </TableRow>
  );

  const int = (v) => Math.round(Number(v || 0));

  // ✅ SAFE NUMBER CONVERTER (ADD THIS ONCE)
  const n = (v) => Number(v || 0);

  // ✅ CALCULATE TOTALS (ROWS / GROUP / GRAND)
  const calculateTotals = (rows) =>
    rows.reduce(
      (acc, r) => {
        acc.trip_charges_days_used += n(r.trip_charges_days_used);
        acc.runKm += n(r.runKm); // ✅ NEW (important)
        acc.trip_charges_hours_used += n(r.trip_charges_hours_used);
        acc.trip_charges_kms_used += n(r.trip_charges_kms_used);
        acc.trip_charges_charge_amount += n(r.trip_charges_charge_amount);

        acc.trip_charges_extra_hour += n(r.trip_charges_extra_hour);
        acc.trip_charges_extra_hour_amount += n(
          r.trip_charges_extra_hour_amount,
        );

        acc.trip_charges_extra_kms += n(r.trip_charges_extra_kms);
        acc.trip_charges_extra_km_amount += n(r.trip_charges_extra_km_amount);

        acc.trip_charges_hire_amount += n(r.trip_charges_hire_amount);
        acc.trip_charges_fuel_esculation_amount += n(
          r.trip_charges_fuel_esculation_amount,
        );

        acc.trip_charges_local_batta += n(r.trip_charges_local_batta);
        acc.trip_charges_no_of_os_batta += n(r.trip_charges_no_of_os_batta);
        acc.trip_charges_os_batta_total_amount += n(
          r.trip_charges_os_batta_total_amount,
        );
        acc.trip_charges_no_of_os_night_batta += n(
          r.trip_charges_no_of_os_night_batta,
        );
        acc.trip_charges_os_night_batta_total_amount += n(
          r.trip_charges_os_night_batta_total_amount,
        );

        acc.trip_charges_permit += n(r.trip_charges_permit);
        acc.trip_charges_parking += n(r.trip_charges_parking);
        acc.trip_charges_others += n(r.trip_charges_others);
        acc.trip_charges_toll_fasttag += n(r.trip_charges_toll_fasttag);
        acc.trip_charges_escort_amount += n(r.trip_charges_escort_amount);

        acc.trip_charges_advance += n(r.trip_charges_advance);
        acc.trip_charges_booking_advance += n(r.trip_charges_booking_advance);

        return acc;
      },
      {
        trip_charges_days_used: 0,
        runKm: 0, // ✅ NEW

        trip_charges_hours_used: 0,
        trip_charges_kms_used: 0,
        trip_charges_charge_amount: 0,

        trip_charges_extra_hour: 0,
        trip_charges_extra_hour_amount: 0,

        trip_charges_extra_kms: 0,
        trip_charges_extra_km_amount: 0,

        trip_charges_hire_amount: 0,
        trip_charges_fuel_esculation_amount: 0,

        trip_charges_local_batta: 0,
        trip_charges_no_of_os_batta: 0,
        trip_charges_os_batta_total_amount: 0,
        trip_charges_no_of_os_night_batta: 0,
        trip_charges_os_night_batta_total_amount: 0,

        trip_charges_permit: 0,
        trip_charges_parking: 0,
        trip_charges_others: 0,
        trip_charges_toll_fasttag: 0,
        trip_charges_escort_amount: 0,

        trip_charges_advance: 0,
        trip_charges_booking_advance: 0,
      },
    );

  const totals = calculateTotals(filteredRows);

  // ✅ FORMAT HOURS/KMS/RATE IN ONE STRING
  // ✅ HOURS / KMS (ONE COLUMN)
  const formatHrsKms = (r) => {
    const hrs = n(r.trip_charges_slab_hour);
    const kms = n(r.trip_charges_slab_km);

    if (!hrs && !kms) return "";
    return `${hrs} / ${kms}`;
  };

  /* ===============================
     PRINT / PDF / EXCEL
  ================================ */
  const exportColumns = [
    { header: "Date", dataKey: "date" },
    { header: "Tripsheet No", dataKey: "tripsheet" },
    { header: "Customer", dataKey: "customer" },
    { header: "Duty", dataKey: "duty" },
    { header: "Vehicle", dataKey: "vehicle" },
    { header: "Vehicle Group", dataKey: "vehicleGroup" },
    { header: "Opening KM", dataKey: "openingKm" },
    { header: "Closing KM", dataKey: "closingKm" },
    { header: "Run KM", dataKey: "runKm" },
    { header: "Days", dataKey: "days" },
    { header: "Hours", dataKey: "hours" },
    { header: "Amount", dataKey: "amount" },
  ];

  const exportRows = filteredRows.map((r) => ({
    date: fmtDate(r.open_date),
    tripsheet: r.tripsheet_number || "",
    customer: r.customer_name || "",
    duty: r.duty || "",
    vehicle: r.vehicle_display || "",
    vehicleGroup: r.assigned_vehicle_group_name || "",
    openingKm: r.open_km_reading ?? "",
    closingKm: r.close_km_reading ?? "",
    runKm: (Number(r.close_km_reading || 0) - Number(r.open_km_reading || 0)),
    days: r.trip_charges_days_used ?? "",
    hours: r.trip_charges_hours_used ?? "",
    amount: r.customer_total ?? r.trip_charges_charge_amount ?? "",
  }));

  const handlePrint = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
    doc.setFontSize(14);
    doc.text(`${reportTitleMap[reportType]}${selectedFinancialYear?.label ? ` (FY ${selectedFinancialYear.label})` : ""}`, 10, 12);
    autoTable(doc, {
      columns: exportColumns,
      body: exportRows,
      startY: 18,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [230, 235, 240], textColor: [20, 30, 40] },
      theme: "grid",
    });
    printPdfDoc(doc);
  };

  const handlePDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
    doc.setFontSize(14);
    doc.text(`${reportTitleMap[reportType]}${selectedFinancialYear?.label ? ` (FY ${selectedFinancialYear.label})` : ""}`, 10, 12);
    autoTable(doc, {
      columns: exportColumns,
      body: exportRows,
      startY: 18,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [230, 235, 240], textColor: [20, 30, 40] },
      theme: "grid",
    });
    doc.save(`Tripsheet_Statement_${selectedFinancialYear?.label || "Report"}.pdf`);
  };

  const handleExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tripsheet Statement");
    XLSX.writeFile(
      workbook,
      `Tripsheet_Statement_${selectedFinancialYear?.label || "Report"}.xlsx`
    );
  };

  /* ===============================
     UI
  ================================ */
  return (
    <Box
      sx={{
        p: 1,
        height: "100%",
        overflowY: "hidden", // ✅ safety
      }}
    >
      <Box
        className="no-print"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
        sx={{ mb: 1.5 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Back to Reports">
            <IconButton
              size="small"
              onClick={() => navigate("/reports")}
              sx={{ border: "1px solid #d7dee7", borderRadius: 1.5 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography fontWeight={700} sx={{ fontSize: "0.95rem" }}>
            Tripsheet Statement
          </Typography>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            onClick={loadReport}
            disabled={loading || financialYearLoading}
            sx={{ minWidth: 90, height: 40, fontWeight: 500 }}
          >
            {loading ? "Loading..." : "Search"}
          </Button>
          <Button variant="outlined" size="small" onClick={handlePrint} disabled={!filteredRows.length} sx={{ minWidth: 90, height: 40 }}>
            Print
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={handlePDF} disabled={!filteredRows.length} sx={{ minWidth: 90, height: 40 }}>
            PDF
          </Button>
          <Button variant="outlined" color="success" size="small" onClick={handleExcel} disabled={!filteredRows.length} sx={{ minWidth: 90, height: 40 }}>
            Excel
          </Button>
        </Box>
      </Box>

      {reportError && <Alert severity="error" sx={{ mb: 1 }}>{reportError}</Alert>}

      <Box sx={{ mb: 1 }}>
        <Typography fontWeight={700} sx={{ textAlign: "left" }}>
          {reportTitleMap[reportType]}
          {selectedFinancialYear?.label ? ` (FY ${selectedFinancialYear.label})` : ""}
        </Typography>

        {/* ✅ SUB TITLE FOR VEHICLE GROUP */}
        {reportType === "VEHICLE_GROUP_WISE" && vehicleGroup !== "ALL" && (
          <Typography
            fontSize="0.75rem"
            color="text.secondary"
            sx={{ textAlign: "left" }}
          >
            Vehicle Group : {vehicleGroup}
          </Typography>
        )}

        {/* DATE RANGE */}
        {(fromDate || toDate) && (
          <Typography
            fontSize="0.75rem"
            color="text.secondary"
            sx={{ textAlign: "left" }}
          >
            {fromDate ? dayjs(fromDate).format("DD-MMM-YYYY") : "From"}{" "}
            &nbsp;:&nbsp;
            {toDate ? dayjs(toDate).format("DD-MMM-YYYY") : "To"}
          </Typography>
        )}
      </Box>

      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <FormControl size="small" sx={compactSelectSx}>
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            label="Report Type"
            onChange={(e) => setReportType(e.target.value)}
          >
            <MenuItem value="DATE_WISE">Date Wise</MenuItem>
            <MenuItem value="NUMBER_WISE">Tripsheet No Wise</MenuItem>
            <MenuItem value="CUSTOMER_WISE">Customer Wise</MenuItem>
            <MenuItem value="VEHICLE_GROUP_WISE">Vehicle Group Wise</MenuItem>
           
          </Select>
        </FormControl>

        {(reportType === "CUSTOMER_WISE") && (
          <FormControl size="small" sx={compactSelectSx}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customer}
              label="Customer"
              onChange={(e) => setCustomer(e.target.value)}
            >
              {customerOptions.map((c) => (
                <MenuItem
                  key={c}
                  value={c}
                  sx={{ fontSize: "0.75rem" }} // 🔽 menu item font
                >
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(reportType === "VEHICLE_GROUP_WISE" ||
          reportType === "CUSTOMER_WISE" 
          ) && (
          <FormControl size="small" sx={compactSelectSx}>
            <InputLabel>Vehicle Group</InputLabel>
            <Select
              value={vehicleGroup}
              label="Vehicle Group"
              onChange={(e) => setVehicleGroup(e.target.value)}
            >
              {vehicleGroupOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
       
        <MyDatePicker
          label="From Date"
          value={fromDate}
          onChange={setFromDate}
          inputStyle={{
            fontSize: FILTER_FONT,
            minHeight: FILTER_HEIGHT,
            height: FILTER_HEIGHT,
            padding: "2px 6px",
          }}
          labelStyle={{
            fontSize: FILTER_FONT,
            lineHeight: 1,
            marginBottom: 0,
            top: -12,
            left: 8,
          }}
          containerStyle={{
            width: 140,
            minHeight: FILTER_HEIGHT,
            height: FILTER_HEIGHT,
            marginBottom: 0,
          }}
        />

        <MyDatePicker
          label="To Date"
          value={toDate}
          onChange={setToDate}
          inputStyle={{
            fontSize: FILTER_FONT,
            minHeight: FILTER_HEIGHT,
            height: FILTER_HEIGHT,
            padding: "2px 6px",
          }}
          labelStyle={{
            fontSize: FILTER_FONT,
            lineHeight: 1,
            marginBottom: 0,
            top: -12,
            left: 8,
          }}
          containerStyle={{
            width: 140,
            minHeight: FILTER_HEIGHT,
            height: FILTER_HEIGHT,
            marginBottom: 0,
          }}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          overflow: "hidden", // ✅ THIS LINE FIXES DOUBLE SCROLL
          background: "#fff",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            // height: "100%",
            // overflow: "auto",
          }}
        >
          {(reportType === "DATE_WISE" ||
            reportType === "NUMBER_WISE" ||
            reportType === "VEHICLE_GROUP_WISE" ||
            reportType === "CUSTOMER_WISE") &&       renderDateWiseTable()}
          </Paper>
      </Box>
    </Box>
  );
}
